import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Activity, AlertCircle, BarChart3, CheckCircle2, Clock, Lightbulb, TrendingDown, TrendingUp, XCircle,
} from 'lucide-react';
import {
  Card, CardHeader, CardTitle, MetricCard, PageHeader, SectionEmptyState,
} from '@/components/ui';
import { GlowAreaChart, type AreaSeries } from '@/components/ui/charts/GlowAreaChart';
import { DonutChart } from '@/components/ui/charts/DonutChart';
import { api } from '@/lib/api/client';
import { isApiError, unwrap } from '@/lib/api/envelope';
import { Endpoints } from '@/lib/api/endpoints';
import type { ResponseEnvelope } from '@/types/api';
import { cn } from '@/lib/utils/cn';

// ── Types ─────────────────────────────────────────────────────────────────────

interface TimeBucket {
  bucket: string;
  total: number;
  success: number;
  failed: number;
}

interface UsageAnalytics {
  series: TimeBucket[];
  byOperator: Record<string, number>;
  byServiceType: Record<string, number>;
  totalRequests: number;
  successfulRequests: number;
  failedRequests: number;
  averageLatencyMs: number;
}

type Granularity = 'DAILY' | 'WEEKLY' | 'MONTHLY';

const SERVICE_LABELS: Record<string, string> = {
  VERIFY_N_PLUS: 'Verify N+', VERIFY_N: 'Verify N',
  OPPORTUNITY: 'Signal', SIGNAL: 'Signal',
  SHIELD: 'Shield', DETECT: 'Detect', TRUST: 'Trust', UNKNOWN: 'Unknown',
};

const SERVICE_COLORS: Record<string, string> = {
  VERIFY_N:      '#60a5fa',
  VERIFY_N_PLUS: '#a78bfa',
  SIGNAL:        '#34d399',
  OPPORTUNITY:   '#34d399',
  SHIELD:        '#10b981',
  DETECT:        '#fb923c',
  TRUST:         '#22d3ee',
  UNKNOWN:       '#64748b',
};

const BAR_COLORS = [
  '#60a5fa', '#34d399', '#a78bfa', '#fbbf24', '#fb923c', '#22d3ee', '#f472b6',
];

const DARK_GLASS = {
  background: 'linear-gradient(135deg, #050e1d 0%, #060f22 100%)',
  borderColor: 'rgba(96,165,250,0.1)',
} as const;

const DARK_DIVIDER = { borderColor: 'rgba(255,255,255,0.06)' } as const;

// ── API ───────────────────────────────────────────────────────────────────────

async function fetchUsage(
  granularity: Granularity,
  from?: string,
  to?: string,
): Promise<UsageAnalytics> {
  const params: Record<string, string> = { granularity };
  if (from) params.from = `${from}T00:00:00Z`;
  if (to) params.to = `${to}T23:59:59Z`;
  const res = await api.get<ResponseEnvelope<UsageAnalytics>>(Endpoints.me.usage, { params });
  return unwrap(res);
}

// ── Sub-components ────────────────────────────────────────────────────────────

function LegendChip({
  color,
  label,
  value,
}: {
  color: string;
  label: string;
  value?: number;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span
        className="h-2 w-2 rounded-full"
        style={{ background: color, boxShadow: `0 0 6px ${color}80` }}
      />
      <span className="text-[11px]" style={{ color: 'rgba(255,255,255,0.45)' }}>
        {label}
      </span>
      {value !== undefined && value > 0 && (
        <span className="text-[11px] font-semibold tabular-nums" style={{ color }}>
          {value.toLocaleString()}
        </span>
      )}
    </div>
  );
}

function BreakdownBar({
  label,
  value,
  total,
  color,
}: {
  label: string;
  value: number;
  total: number;
  color: string;
}) {
  const pct = total === 0 ? 0 : (value / total) * 100;
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between text-xs">
        <span className="font-medium text-fg">{label}</span>
        <span className="tabular-nums text-fg-muted">
          {value.toLocaleString()}
          <span className="ml-1.5 text-[10px] text-fg-subtle">{pct.toFixed(1)}%</span>
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-subtle">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{
            width: `${pct}%`,
            background: color,
            boxShadow: `0 0 8px ${color}60`,
          }}
        />
      </div>
    </div>
  );
}

function InsightChip({
  icon: Icon,
  label,
  value,
  tone = 'default',
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  tone?: 'default' | 'success' | 'warning' | 'danger';
}) {
  const colors = {
    default: 'bg-primary-50 text-primary-700 dark:bg-primary-500/10 dark:text-primary-300',
    success: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300',
    warning: 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300',
    danger:  'bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300',
  };
  return (
    <div className={cn('flex items-start gap-2.5 rounded-xl p-3', colors[tone])}>
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wide opacity-70">{label}</p>
        <p className="mt-0.5 text-xs font-semibold leading-tight">{value}</p>
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function ClientUsageAnalyticsPage() {
  const [granularity, setGranularity] = useState<Granularity>('DAILY');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const { data: analytics, isLoading, isError, error } = useQuery({
    queryKey: ['me', 'usage', granularity, fromDate, toDate],
    queryFn: () => fetchUsage(granularity, fromDate || undefined, toDate || undefined),
    staleTime: 300_000,
  });

  const successRate =
    analytics && analytics.totalRequests > 0
      ? (analytics.successfulRequests / analytics.totalRequests) * 100
      : 0;

  // Chart data
  const chartSeries: AreaSeries[] = [
    {
      key: 'match',
      label: 'Match',
      values: (analytics?.series ?? []).map((b) => b.success),
      lineColor: '#60a5fa',
      areaColor: '#3b82f6',
    },
    {
      key: 'nomatch',
      label: 'No match',
      values: (analytics?.series ?? []).map((b) => b.failed),
      lineColor: '#fb7185',
      areaColor: '#f43f5e',
    },
  ];
  const chartLabels = (analytics?.series ?? []).map((b) => b.bucket);
  const hasChartData = (analytics?.series ?? []).some((b) => b.total > 0);

  const totalMatch = chartSeries[0]?.values.reduce((s, v) => s + v, 0) ?? 0;
  const totalNoMatch = chartSeries[1]?.values.reduce((s, v) => s + v, 0) ?? 0;

  // Key insights
  const peakBucket = analytics?.series.reduce(
    (best, b) => (b.total > (best?.total ?? 0) ? b : best),
    null as TimeBucket | null,
  );
  const dailyAvg =
    analytics && analytics.series.length > 0
      ? Math.round(analytics.totalRequests / analytics.series.length)
      : 0;
  const topOperator = analytics
    ? Object.entries(analytics.byOperator).sort((a, b) => b[1] - a[1])[0]
    : null;
  const topService = analytics
    ? Object.entries(analytics.byServiceType).sort((a, b) => b[1] - a[1])[0]
    : null;

  // Service donut segments
  const serviceSegments = analytics
    ? Object.entries(analytics.byServiceType)
        .filter(([, v]) => v > 0)
        .sort((a, b) => b[1] - a[1])
        .map(([key, val]) => ({
          label: SERVICE_LABELS[key] ?? key,
          value: val,
          color: SERVICE_COLORS[key] ?? '#64748b',
        }))
    : [];
  const serviceTotal = serviceSegments.reduce((s, seg) => s + seg.value, 0);

  function clearDates() {
    setFromDate('');
    setToDate('');
  }

  if (isError) {
    return (
      <div className="space-y-6">
        <PageHeader title="Usage Analytics" subtitle="API activity for your workspace." />
        <div className="flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 dark:border-rose-500/20 dark:bg-rose-500/10">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
          <p className="text-sm text-rose-700 dark:text-rose-300">
            {isApiError(error) ? error.firstErrorMessage : 'Could not load usage analytics.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Usage Analytics"
        subtitle="API activity, service breakdown, and usage trends."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {/* Date range */}
            <div className="hidden items-center gap-1.5 sm:flex">
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="h-8 rounded-lg border border-border bg-surface px-2 text-xs text-fg focus:outline-none focus:ring-2 focus:ring-primary-500/30"
              />
              <span className="text-xs text-fg-muted">–</span>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="h-8 rounded-lg border border-border bg-surface px-2 text-xs text-fg focus:outline-none focus:ring-2 focus:ring-primary-500/30"
              />
              {(fromDate || toDate) && (
                <button
                  type="button"
                  onClick={clearDates}
                  className="text-xs text-fg-muted hover:text-fg"
                >
                  Clear
                </button>
              )}
            </div>
            {/* Granularity */}
            <div className="flex overflow-hidden rounded-xl border border-border bg-surface text-xs font-medium">
              {(['DAILY', 'WEEKLY', 'MONTHLY'] as Granularity[]).map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGranularity(g)}
                  className={cn(
                    'px-3 py-1.5 transition-colors',
                    granularity === g ? 'bg-primary-500 text-white' : 'text-fg-muted hover:text-fg',
                  )}
                >
                  {g.charAt(0) + g.slice(1).toLowerCase()}
                </button>
              ))}
            </div>
          </div>
        }
      />

      {/* KPI row */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label="Total Requests"
          value={analytics?.totalRequests ?? 0}
          icon={Activity}
          tone="default"
          loading={isLoading}
          sparkline={analytics?.series.map((b) => b.total)}
          className="stagger-1"
        />
        <MetricCard
          label="Successful"
          value={analytics?.successfulRequests ?? 0}
          icon={CheckCircle2}
          tone="success"
          loading={isLoading}
          sparkline={analytics?.series.map((b) => b.success)}
          className="stagger-2"
        />
        <MetricCard
          label="No Match"
          value={analytics?.failedRequests ?? 0}
          icon={XCircle}
          tone="danger"
          loading={isLoading}
          className="stagger-3"
        />
        <MetricCard
          label="Success Rate"
          value={analytics ? `${successRate.toFixed(1)}%` : '—'}
          subtitle={
            analytics ? `${analytics.averageLatencyMs.toFixed(0)} ms avg latency` : undefined
          }
          icon={Clock}
          tone={successRate >= 90 ? 'success' : successRate >= 70 ? 'warning' : 'danger'}
          loading={isLoading}
          className="stagger-4"
        />
      </div>

      {/* Key insights */}
      {!isLoading && analytics && analytics.totalRequests > 0 && (
        <div>
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-fg">
            <Lightbulb className="h-4 w-4 text-amber-500" aria-hidden />
            Key Insights
          </h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {peakBucket && (
              <InsightChip
                icon={TrendingUp}
                label="Peak period"
                value={`${peakBucket.bucket} · ${peakBucket.total.toLocaleString()} requests`}
                tone="default"
              />
            )}
            <InsightChip
              icon={Activity}
              label={`Avg / ${
                granularity === 'DAILY' ? 'day' : granularity === 'WEEKLY' ? 'week' : 'month'
              }`}
              value={`${dailyAvg.toLocaleString()} requests`}
              tone="default"
            />
            {topOperator && (
              <InsightChip
                icon={BarChart3}
                label="Top operator"
                value={`${topOperator[0]} · ${topOperator[1].toLocaleString()}`}
                tone="default"
              />
            )}
            {topService && (
              <InsightChip
                icon={successRate < 80 ? TrendingDown : TrendingUp}
                label="Top service"
                value={`${SERVICE_LABELS[topService[0]] ?? topService[0]} · ${topService[1].toLocaleString()}`}
                tone={successRate < 80 ? 'warning' : 'success'}
              />
            )}
          </div>
        </div>
      )}

      {/* Main chart — dark glass, centerpiece */}
      <div className="overflow-hidden rounded-2xl border" style={DARK_GLASS}>
        {/* Chart header */}
        <div
          className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4"
          style={DARK_DIVIDER}
        >
          <div>
            <h2 className="text-sm font-semibold text-white">
              Request volume
              <span
                className="ml-2 text-xs font-normal capitalize"
                style={{ color: 'rgba(255,255,255,0.35)' }}
              >
                ({granularity.toLowerCase()})
              </span>
            </h2>
            <p className="mt-0.5 text-[11px]" style={{ color: 'rgba(255,255,255,0.35)' }}>
              Match vs. No match requests over time
            </p>
          </div>
          <div className="flex items-center gap-4">
            <LegendChip color="#60a5fa" label="Match" value={totalMatch} />
            <LegendChip color="#fb7185" label="No match" value={totalNoMatch} />
          </div>
        </div>

        {/* Chart */}
        <div className="px-2 py-4">
          {isLoading ? (
            <div
              className="w-full animate-pulse rounded-lg"
              style={{
                height: 200,
                background: 'linear-gradient(180deg, rgba(96,165,250,0.08) 0%, transparent 100%)',
              }}
            />
          ) : !hasChartData ? (
            <SectionEmptyState
              icon={BarChart3}
              title="No activity yet"
              description="API requests will appear here once your integration starts sending traffic."
              className="py-16"
            />
          ) : (
            <GlowAreaChart series={chartSeries} labels={chartLabels} height={200} />
          )}
        </div>

        {/* Footer mini stats */}
        {hasChartData && (
          <div className="grid grid-cols-3 border-t" style={DARK_DIVIDER}>
            <div className="px-5 py-3">
              <p
                className="text-[10px] font-semibold uppercase tracking-widest"
                style={{ color: 'rgba(255,255,255,0.3)' }}
              >
                Match
              </p>
              <p className="mt-1 text-lg font-bold tabular-nums" style={{ color: '#60a5fa' }}>
                {totalMatch.toLocaleString()}
              </p>
            </div>
            <div className="border-l px-5 py-3" style={DARK_DIVIDER}>
              <p
                className="text-[10px] font-semibold uppercase tracking-widest"
                style={{ color: 'rgba(255,255,255,0.3)' }}
              >
                No match
              </p>
              <p className="mt-1 text-lg font-bold tabular-nums" style={{ color: '#fb7185' }}>
                {totalNoMatch.toLocaleString()}
              </p>
            </div>
            <div className="border-l px-5 py-3" style={DARK_DIVIDER}>
              <p
                className="text-[10px] font-semibold uppercase tracking-widest"
                style={{ color: 'rgba(255,255,255,0.3)' }}
              >
                Success rate
              </p>
              <p
                className="mt-1 text-lg font-bold tabular-nums"
                style={{
                  color:
                    successRate >= 90 ? '#34d399' : successRate >= 70 ? '#fbbf24' : '#fb7185',
                }}
              >
                {analytics ? `${successRate.toFixed(1)}%` : '—'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Breakdowns — service donut + operator bars */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* By service — donut */}
        <div className="overflow-hidden rounded-2xl border" style={DARK_GLASS}>
          <div className="flex items-center justify-between border-b px-5 py-4" style={DARK_DIVIDER}>
            <div>
              <h2 className="text-sm font-semibold text-white">By service</h2>
              {analytics && (
                <p className="mt-0.5 text-[11px]" style={{ color: 'rgba(255,255,255,0.35)' }}>
                  {serviceSegments.length} service{serviceSegments.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          </div>
          <div className="p-5">
            {isLoading ? (
              <div className="flex flex-col items-center gap-4">
                <div
                  className="h-36 w-36 animate-pulse rounded-full"
                  style={{ background: 'rgba(255,255,255,0.04)' }}
                />
              </div>
            ) : serviceSegments.length > 0 ? (
              <DonutChart
                segments={serviceSegments}
                size={152}
                thickness={18}
                centerValue={serviceTotal}
                centerLabel="Total"
              />
            ) : (
              <SectionEmptyState title="No service data" className="py-6" />
            )}
          </div>
        </div>

        {/* By operator — bars */}
        <Card elevated>
          <CardHeader>
            <CardTitle>By operator</CardTitle>
            {analytics && (
              <span className="text-xs text-fg-muted">
                {Object.keys(analytics.byOperator).length} operators
              </span>
            )}
          </CardHeader>
          {isLoading ? (
            <div className="space-y-4">
              {[0, 1, 2].map((i) => (
                <div key={i} className="space-y-1.5">
                  <div className="h-3.5 w-32 animate-pulse rounded bg-surface-muted" />
                  <div className="h-1.5 w-full animate-pulse rounded-full bg-surface-muted" />
                </div>
              ))}
            </div>
          ) : analytics && Object.keys(analytics.byOperator).length > 0 ? (
            <div className="space-y-4">
              {Object.entries(analytics.byOperator)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 8)
                .map(([key, val], i) => (
                  <BreakdownBar
                    key={key}
                    label={key === 'UNKNOWN' ? 'Unknown' : key}
                    value={val}
                    total={analytics.totalRequests}
                    color={BAR_COLORS[i % BAR_COLORS.length]!}
                  />
                ))}
            </div>
          ) : (
            <SectionEmptyState title="No operator data" className="py-6" />
          )}
        </Card>
      </div>

      {/* Period breakdown table */}
      {!isLoading && analytics && analytics.series.length > 0 && (
        <Card elevated>
          <CardHeader>
            <CardTitle>Period breakdown</CardTitle>
            <span className="text-xs text-fg-muted">Last {analytics.series.length} periods</span>
          </CardHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border">
                  {['Period', 'Total', 'Match', 'No match', 'Rate'].map((h, i) => (
                    <th
                      key={h}
                      className={cn(
                        'px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-fg-muted',
                        i === 0 ? 'text-left' : 'text-right',
                      )}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {analytics.series
                  .slice(-10)
                  .reverse()
                  .map((b) => {
                    const rate = b.total === 0 ? 0 : (b.success / b.total) * 100;
                    return (
                      <tr
                        key={b.bucket}
                        className="border-b border-border last:border-0 transition-colors hover:bg-surface-muted/40"
                      >
                        <td className="px-4 py-2.5 font-medium text-fg">{b.bucket}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-fg">
                          {b.total.toLocaleString()}
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-emerald-600 dark:text-emerald-400">
                          {b.success.toLocaleString()}
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-rose-500">
                          {b.failed.toLocaleString()}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <span
                            className={cn(
                              'font-semibold tabular-nums',
                              rate >= 90
                                ? 'text-emerald-600 dark:text-emerald-400'
                                : rate >= 70
                                ? 'text-amber-600 dark:text-amber-400'
                                : 'text-rose-500',
                            )}
                          >
                            {rate.toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
